import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Stack, TextField, FormControlLabel,
  Divider, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton, FormControl, InputLabel,
  Select, MenuItem, Grid, CircularProgress, Alert, Switch, Chip,
  ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Receipt as ReceiptIcon, Business as BusinessIcon, ShoppingCart as ShoppingCartIcon,
  Add as AddIcon, Delete as DeleteIcon, Calculate as CalculateIcon,
  Cancel as CancelIcon, Save as SaveIcon, Close as CloseIcon, Payments as PaymentsIcon
} from '@mui/icons-material';

import { useCompany } from '../context/CompanyContext';
import { ItemSearchModal } from '../components/ItemSearchModal';
import { ClientPicker } from '../components/ClientPicker';
import VatExemptionAutocomplete from '../components/VatExemptionAutocomplete';
import {
  GET_CLIENTS, GET_NEXT_DOCUMENT_NUMBER, GET_ACTIVE_VAT_RATES,
  GET_EXCHANGE_RATE_FOR_CURRENCY, GET_ACTIVE_ITEMS_BY_COMPANY, GET_ACTIVE_CURRENCIES,
  GET_PAYMENT_METHODS_BY_COMPANY, GET_BANK_ACCOUNTS_BY_COMPANY,
  GET_ALL_VAT_EXEMPTION_REASONS, GET_DOCUMENTS_BY_COMPANY_PAGED, GET_COMPANY_DETAILS,
  GET_NOMENCLATURES
} from '../graphql/queries';
import { CREATE_DOCUMENT } from '../graphql/mutations';

// Constants
const DocumentType = {
  INVOICE: 'INVOICE',
  PROFORMA: 'PROFORMA',
  CREDIT_NOTE: 'CREDIT_NOTE',
  DEBIT_NOTE: 'DEBIT_NOTE',
};

const DOCUMENT_TYPE_LABELS = {
  INVOICE: 'Фактура',
  PROFORMA: 'Проформа',
  CREDIT_NOTE: 'Кредитно известие',
  DEBIT_NOTE: 'Дебитно известие',
};

const numberInputSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
  '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
};

const Section = ({ icon, title, subtitle, action, children }) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ lineHeight: 1.25 }}>{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
    {children}
  </Paper>
);

const CreateInvoiceNew = () => {
  const navigate = useNavigate();
  const { activeCompanyId } = useCompany();

  const [loading, setLoading] = useState(false);
  const [isItemSearchModalOpen, setIsItemSearchModalOpen] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [newItemNotification, setNewItemNotification] = useState(null);
  const [clientError, setClientError] = useState(false);
  const [pricesIncludeVat, setPricesIncludeVat] = useState(false);

  const [formData, setFormData] = useState({
    documentType: DocumentType.INVOICE,
    number: '',
    issueDate: new Date().toISOString().split('T')[0],
    vatDate: new Date().toISOString().split('T')[0],
    clientId: '',
    clientName: '',
    clientBulstat: '',
    clientVatNumber: '',
    clientAddress: '',
    items: [
      {
        id: Date.now().toString(),
        itemId: null, // Real item ID from database
        articleNumber: '',
        description: '',
        quantity: 1,
        unit: 'C62', // Default Unit Code
        priceWithoutVat: 0,
        priceWithVat: 0,
        totalValue: 0,
        vatPercent: 20, // Default VAT
        vatExemptionReasonId: null, // Per-item VAT exemption
      },
    ],
    subtotalWithoutDiscount: 0,
    discountPercent: 0,
    discountAmount: 0,
    taxableAmount: 0,
    vatPercent: 20, // Deprecated, kept for backward compatibility if needed
    vatAmount: 0,
    vatBreakdown: {}, // { rate: { base: X, amount: Y } }
    totalAmount: 0,
    totalAmountInEUR: 0,
    notes: '',
    paymentTerms: 15,
    currencyCode: 'EUR',
    paymentMethodId: '',
    bankAccountId: '',
    vatExemptionReasonId: '',
    conversionRate: 1,
    conversionRateDate: null,
  });

  // Calculate Progress
  const calculateProgress = () => {
    let progress = 0;
    if (formData.clientId) progress += 25;
    if (formData.items.some(i => i.quantity > 0 && i.priceWithoutVat > 0)) progress += 25;
    if (formData.paymentMethodId) progress += 25;
    if (formData.totalAmount > 0) progress += 25;
    return progress;
  };

  const progress = calculateProgress();

  // GraphQL Queries
  const { data: companyData } = useQuery(GET_COMPANY_DETAILS, {
    variables: { id: activeCompanyId },
    skip: !activeCompanyId,
  });

  const { refetch: refetchClients } = useQuery(GET_CLIENTS, {
    variables: { companyId: activeCompanyId },
    skip: !activeCompanyId,
  });

  const [getNextNumber, { data: nextNumberData, loading: nextNumberLoading }] = useLazyQuery(GET_NEXT_DOCUMENT_NUMBER, {
    fetchPolicy: 'no-cache',
  });
  const { data: vatRatesData } = useQuery(GET_ACTIVE_VAT_RATES);
  const [getExchangeRate, { data: exchangeRateData, loading: exchangeRateLoading }] = useLazyQuery(GET_EXCHANGE_RATE_FOR_CURRENCY);
  const { data: itemsData, loading: itemsLoading, refetch: refetchItems } = useQuery(GET_ACTIVE_ITEMS_BY_COMPANY, {
    variables: { companyId: activeCompanyId },
    skip: !activeCompanyId,
  });
  const { data: currenciesData, loading: currenciesLoading } = useQuery(GET_ACTIVE_CURRENCIES);
  const { data: paymentMethodsData, loading: paymentMethodsLoading } = useQuery(GET_PAYMENT_METHODS_BY_COMPANY, {
    variables: { companyId: activeCompanyId },
    skip: !activeCompanyId,
  });
  const { data: bankAccountsData, loading: bankAccountsLoading } = useQuery(GET_BANK_ACCOUNTS_BY_COMPANY, {
    variables: { companyId: activeCompanyId },
    skip: !activeCompanyId,
  });
  const { data: exemptionReasonsData } = useQuery(GET_ALL_VAT_EXEMPTION_REASONS);
  const { data: nomenclaturesData } = useQuery(GET_NOMENCLATURES);

  // Helper to get unit name from code
  const getUnitName = (code) => {
    const unit = nomenclaturesData?.allUnitsOfMeasure?.find(u => u.code === code);
    return unit ? `${unit.name} (${unit.symbol})` : code;
  };

  const [createDocument] = useMutation(
    CREATE_DOCUMENT,
    {
      refetchQueries: [
        {
          query: GET_DOCUMENTS_BY_COMPANY_PAGED,
          variables: { companyId: activeCompanyId, page: 0, size: 25, search: null }
        }
      ],
      awaitRefetchQueries: true
    }
  );

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // Effects
  useEffect(() => {
    if (activeCompanyId && formData.documentType) {
      getNextNumber({ variables: { companyId: activeCompanyId, documentType: formData.documentType } });
    }
  }, [activeCompanyId, formData.documentType, getNextNumber]);

  useEffect(() => {
    if (nextNumberData?.nextDocumentNumber) {
      setFormData(prev => ({ ...prev, number: nextNumberData.nextDocumentNumber }));
    }
  }, [nextNumberData]);

  // Set default payment terms and VAT status from company settings
  useEffect(() => {
    if (companyData?.companyById) {
      const company = companyData.companyById;
      const isVatRegistered = company.isVatRegistered || false;
      
      setFormData(prev => {
        const newData = { 
          ...prev, 
          paymentTerms: company.defaultPaymentTerms || prev.paymentTerms 
        };
        
        // If company is not VAT registered, set initial items to 0% VAT
        if (!isVatRegistered && prev.items.length === 1 && prev.items[0].priceWithoutVat === 0) {
          const smReason = exemptionReasonsData?.allVatExemptionReasons?.find(r => r.reasonCode === 'VATEX-EU-SM');
          newData.items = [{
            ...prev.items[0],
            vatPercent: 0,
            vatExemptionReasonId: smReason?.id || null
          }];
        }
        
        return newData;
      });
    }
  }, [companyData, exemptionReasonsData]);

  useEffect(() => {
    if (formData.currencyCode === 'EUR') {
      setFormData(prev => ({
        ...prev,
        conversionRate: 1,
        conversionRateDate: null,
      }));
    } else if (formData.currencyCode && formData.issueDate) {
      getExchangeRate({ variables: { currencyCode: formData.currencyCode, date: formData.issueDate } });
    }
  }, [formData.currencyCode, formData.issueDate, getExchangeRate]);

  useEffect(() => {
    if (exchangeRateData?.exchangeRateForCurrency) {
      setFormData(prev => ({
        ...prev,
        conversionRate: exchangeRateData.exchangeRateForCurrency.rate,
        conversionRateDate: exchangeRateData.exchangeRateForCurrency.rateDate,
      }));
    }
  }, [exchangeRateData]);

  const calculateTotals = useCallback(() => {
    const subtotalWithoutDiscount = formData.items.reduce((sum, item) => sum + item.totalValue, 0);
    const discountAmount = (subtotalWithoutDiscount * formData.discountPercent) / 100;
    const taxableAmount = subtotalWithoutDiscount - discountAmount;
    
    // Calculate VAT per item and group by rate
    const vatBreakdown = {};
    let totalVatAmount = 0;

    formData.items.forEach(item => {
        const itemVatRate = item.vatPercent !== undefined ? item.vatPercent : 0;
        const itemNet = item.totalValue;
        
        // Apply global discount proportionally to the net amount
        // Note: This assumes the global discount applies to the net amount before VAT
        const itemDiscountedNet = itemNet * (1 - formData.discountPercent / 100);
        const itemVat = itemDiscountedNet * itemVatRate / 100;
        
        totalVatAmount += itemVat;
        
        if (!vatBreakdown[itemVatRate]) {
            vatBreakdown[itemVatRate] = {
                rate: itemVatRate,
                base: 0,
                amount: 0
            };
        }
        vatBreakdown[itemVatRate].base += itemDiscountedNet;
        vatBreakdown[itemVatRate].amount += itemVat;
    });

    const totalAmount = taxableAmount + totalVatAmount;

    let totalAmountInEUR = totalAmount;

    if (formData.currencyCode !== 'EUR' && formData.conversionRate && formData.conversionRate !== 0) {
      // ЕЦБ курс: 1 EUR = X единици валута, затова делим за да получим EUR
      totalAmountInEUR = totalAmount / formData.conversionRate;
    }

    setFormData(prev => ({
      ...prev,
      subtotalWithoutDiscount,
      discountAmount,
      taxableAmount,
      vatAmount: totalVatAmount,
      vatBreakdown,
      totalAmount,
      totalAmountInEUR,
    }));
  }, [formData.items, formData.discountPercent, formData.currencyCode, formData.conversionRate]);

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Clear VAT exemption reason if VAT is not 0%
      if (field === 'vatPercent' && value !== 0) {
        newData.vatExemptionReasonId = '';
      }
      return newData;
    });
  };

  const handleDecimalInput = (value) => {
    return parseFloat(value.replace(',', '.')) || 0;
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    const currentItem = { ...newItems[index], [field]: value };

    // Need access to current item's VAT rate for price calculation
    // If we are changing VAT percent, use the new value, otherwise use existing
    const vatRate = (field === 'vatPercent' ? value : currentItem.vatPercent) / 100;

    if (field === 'quantity' || field === 'priceWithoutVat' || field === 'priceWithVat' || field === 'vatPercent') {
      
      if (field === 'priceWithVat' && pricesIncludeVat) {
        // Calculate price without VAT from price with VAT
        const priceWithVat = value;
        const priceWithoutVat = priceWithVat / (1 + vatRate);
        currentItem.priceWithoutVat = Math.round(priceWithoutVat * 100) / 100;
        currentItem.priceWithVat = priceWithVat;
      } else if (field === 'priceWithoutVat' && !pricesIncludeVat) {
        // Calculate price with VAT from price without VAT
        const priceWithoutVat = value;
        const priceWithVat = priceWithoutVat * (1 + vatRate);
        currentItem.priceWithoutVat = priceWithoutVat;
        currentItem.priceWithVat = Math.round(priceWithVat * 100) / 100;
      } else if (field === 'vatPercent') {
        // Recalculate based on pricesIncludeVat mode
         if (pricesIncludeVat) {
             // Keep priceWithVat constant, update priceWithoutVat
             const priceWithoutVat = currentItem.priceWithVat / (1 + vatRate);
             currentItem.priceWithoutVat = Math.round(priceWithoutVat * 100) / 100;
         } else {
             // Keep priceWithoutVat constant, update priceWithVat
             const priceWithVat = currentItem.priceWithoutVat * (1 + vatRate);
             currentItem.priceWithVat = Math.round(priceWithVat * 100) / 100;
         }
         // Also clear exemption reason if VAT > 0
         if (value !== 0) {
             currentItem.vatExemptionReasonId = null;
         } else {
             // If VAT changed to 0%, try to auto-select Art. 113 for non-VAT registered companies
             if (companyData?.companyById && !companyData.companyById.isVatRegistered) {
               const smReason = exemptionReasonsData?.allVatExemptionReasons?.find(r => r.reasonCode === 'VATEX-EU-SM');
               currentItem.vatExemptionReasonId = smReason?.id || null;
             }
         }
      }

      currentItem.totalValue = currentItem.quantity * currentItem.priceWithoutVat;
    }
    
    newItems[index] = currentItem;
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  // Trigger recalculation when pricesIncludeVat changes
  useEffect(() => {
    if (formData.items.length > 0) {
      const newItems = formData.items.map(item => {
        const vatRate = (item.vatPercent || 0) / 100;
        let priceWithVat = item.priceWithVat;
        let priceWithoutVat = item.priceWithoutVat;

        if (pricesIncludeVat) {
           priceWithoutVat = item.priceWithVat / (1 + vatRate);
           priceWithoutVat = Math.round(priceWithoutVat * 100) / 100;
        } else {
           priceWithVat = item.priceWithoutVat * (1 + vatRate);
           priceWithVat = Math.round(priceWithVat * 100) / 100;
        }
        
        return {
          ...item,
          priceWithoutVat,
          priceWithVat,
          totalValue: item.quantity * priceWithoutVat
        };
      });
      setFormData(prev => ({ ...prev, items: newItems }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesIncludeVat]);


  const addItem = () => {
    let defaultRate = vatRatesData?.activeVatRates?.find(r => r.isDefault)?.rateValue || 20;
    let defaultReasonId = null;

    if (companyData?.companyById && !companyData.companyById.isVatRegistered) {
      defaultRate = 0;
      const smReason = exemptionReasonsData?.allVatExemptionReasons?.find(r => r.reasonCode === 'VATEX-EU-SM');
      defaultReasonId = smReason?.id || null;
    }

    const newItem = {
      id: Date.now().toString(),
      itemId: null, // Real item ID from database
      articleNumber: '',
      description: '',
      quantity: 0,
      unit: 'C62', // Default to 'брой' code
      priceWithoutVat: 0,
      priceWithVat: 0,
      totalValue: 0,
      vatPercent: defaultRate,
      vatExemptionReasonId: defaultReasonId, // Per-item VAT exemption
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };


  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: newItems }));
    }
  };

  const openItemSearchModal = (index) => {
    setCurrentItemIndex(index);
    setIsItemSearchModalOpen(true);
  };

  const handleClientSelected = (client) => {
    if (!client) {
      setFormData(prev => ({
        ...prev,
        clientId: '',
        clientName: '',
        clientBulstat: '',
        clientVatNumber: '',
        clientAddress: '',
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      clientId: client.id,
      clientName: client.name,
      clientBulstat: client.eik || '',
      clientVatNumber: client.vatNumber || '',
      clientAddress: client.address || '',
    }));
    setClientError(false);
  };

  const handleItemSelected = (item) => {
    if (currentItemIndex !== null) {
      const newItems = [...formData.items];
      const currentItem = newItems[currentItemIndex];

      const priceWithoutVat = item.unitPrice || 0;
      const vatRate = formData.vatPercent / 100;
      const priceWithVat = Math.round(priceWithoutVat * (1 + vatRate) * 100) / 100;

      const updatedItem = {
        ...currentItem,
        itemId: item.id, // Real item ID from database
        articleNumber: item.itemNumber || '',
        description: item.name,
        priceWithoutVat: priceWithoutVat,
        priceWithVat: priceWithVat,
        // Map item UoM to standard code if possible, else default
        unit: item.unitOfMeasure || 'C62', 
        totalValue: currentItem.quantity * priceWithoutVat,
      };

      newItems[currentItemIndex] = updatedItem;

      setFormData(prev => ({ ...prev, items: newItems }));

      // Затваряме модала и изчистваме индекса
      setIsItemSearchModalOpen(false);
      setCurrentItemIndex(null);
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!formData.clientId) {
        setClientError(true);
        alert("Грешка: Моля, изберете клиент.");
        setLoading(false);
        return;
      }

      // Validate that all items have been selected from catalog
      const unselectedItems = formData.items.filter(item => !item.itemId);
      if (unselectedItems.length > 0) {
        alert("Грешка: Моля, изберете артикул от каталога за всеки ред.");
        setLoading(false);
        return;
      }

      // Prepare document items for the mutation
      const items = formData.items.map(item => ({
        itemId: item.itemId,
        quantity: item.quantity,
        unitPrice: item.priceWithoutVat,
        vatRate: item.vatPercent !== undefined ? item.vatPercent : 20,
        itemDescription: item.description || item.articleNumber,
        ...(item.vatExemptionReasonId && { vatExemptionReasonId: item.vatExemptionReasonId })
      }));

      // Check if document is a tax document (needs VAT date)
      const isTaxDocument = formData.documentType !== DocumentType.PROFORMA;

      // Prepare the input for the mutation
      const input = {
        documentType: formData.documentType,
        issueDate: formData.issueDate,
        ...(isTaxDocument && { vatDate: formData.vatDate }),
        dueDate: new Date(new Date(formData.issueDate).getTime() + formData.paymentTerms * 24 * 60 * 60 * 1000)
          .toISOString().split('T')[0],
        companyId: activeCompanyId,
        clientId: formData.clientId,
        items: items,
        currencyCode: formData.currencyCode,
        ...(formData.notes && { notes: formData.notes }),
        ...(formData.paymentMethodId && { paymentMethodId: formData.paymentMethodId }),
        ...(formData.bankAccountId && { bankAccountId: formData.bankAccountId })
      };

      console.log('Creating document with input:', JSON.stringify(input, null, 2));
      console.log('formData.documentType:', formData.documentType);

      const { data } = await createDocument({
        variables: { input }
      });

      console.log('Document created successfully:', data.createDocument);
      window.scrollTo(0, 0);
      navigate('/documents');
    } catch (error) {
      console.error('Error creating document:', error);
      if (error.message.includes("Item not found")) {
        alert("Грешка: Един или повече от артикулите не е избран от каталога. Моля, изберете артикул за всеки ред.");
      } else {
        alert(`Грешка при създаване на документ: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = formData.clientId
    ? {
        id: formData.clientId,
        name: formData.clientName,
        eik: formData.clientBulstat,
        vatNumber: formData.clientVatNumber,
        address: formData.clientAddress,
      }
    : null;

  const isTaxDocument = formData.documentType !== DocumentType.PROFORMA;
  const completionSteps = [
    { label: 'Клиент', done: Boolean(formData.clientId) },
    { label: 'Артикули', done: formData.items.some((item) => item.quantity > 0 && item.priceWithoutVat > 0) },
    { label: 'Плащане', done: Boolean(formData.paymentMethodId) },
    { label: 'Сума', done: formData.totalAmount > 0 },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      {newItemNotification && (
        <Alert
          severity="info"
          sx={{
            position: 'fixed',
            top: 80,
            right: 20,
            zIndex: 1300,
            minWidth: 300,
            maxWidth: 500
          }}
          onClose={() => setNewItemNotification(null)}
        >
          {newItemNotification}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Нов документ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {DOCUMENT_TYPE_LABELS[formData.documentType]}
            {formData.number ? ` № ${formData.number}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {completionSteps.map((step) => (
            <Chip
              key={step.label}
              size="small"
              label={step.label}
              color={step.done ? 'success' : 'default'}
              variant={step.done ? 'filled' : 'outlined'}
            />
          ))}
          <Chip size="small" label={`${progress}%`} color={progress === 100 ? 'success' : 'primary'} />
        </Stack>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          <Section
            icon={<ReceiptIcon />}
            title="Документ"
            subtitle="Тип, номер и дати"
          >
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={formData.documentType}
              onChange={(_, value) => value && handleInputChange('documentType', value)}
              sx={{
                mb: 2.5,
                flexWrap: { xs: 'wrap', md: 'nowrap' },
                '& .MuiToggleButton-root': {
                  py: 1.1,
                  fontWeight: 600,
                  textTransform: 'none',
                  flex: { xs: '1 1 45%', md: 1 },
                },
              }}
            >
              <ToggleButton value={DocumentType.PROFORMA}>Проформа</ToggleButton>
              <ToggleButton value={DocumentType.INVOICE}>Фактура</ToggleButton>
              <ToggleButton value={DocumentType.DEBIT_NOTE}>Дебитно известие</ToggleButton>
              <ToggleButton value={DocumentType.CREDIT_NOTE}>Кредитно известие</ToggleButton>
            </ToggleButtonGroup>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Номер на документ"
                value={nextNumberLoading ? 'Зареждане...' : formData.number}
                fullWidth
                disabled
                placeholder="Генерира се автоматично"
              />
              <TextField
                label="Дата на издаване"
                type="date"
                value={formData.issueDate}
                onChange={(e) => handleInputChange('issueDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              {isTaxDocument && (
                <TextField
                  label="Дата на данъчно събитие"
                  type="date"
                  value={formData.vatDate}
                  onChange={(e) => handleInputChange('vatDate', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Stack>
          </Section>

          <Section
            icon={<BusinessIcon />}
            title="Клиент"
            subtitle="Търсене по име, ЕИК или ДДС номер"
          >
            <ClientPicker
              value={selectedClient}
              onChange={handleClientSelected}
              onClientAdded={refetchClients}
              error={clientError}
              helperText={clientError ? 'Клиентът е задължителен' : undefined}
            />
          </Section>

          <Section
            icon={<ShoppingCartIcon />}
            title="Артикули и услуги"
            subtitle="Каталог, количества, цени и ДДС"
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={addItem} size="small">
                Добави ред
              </Button>
            }
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Валута</InputLabel>
                <Select
                  value={formData.currencyCode}
                  label="Валута"
                  onChange={(e) => handleInputChange('currencyCode', e.target.value)}
                  disabled={currenciesLoading}
                >
                  {currenciesData?.activeCurrencies?.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.code} ({currency.name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={pricesIncludeVat}
                    onChange={(e) => setPricesIncludeVat(e.target.checked)}
                    color="primary"
                  />
                }
                label="Цените са с включено ДДС"
              />
            </Stack>
            <TableContainer
              component={Paper}
              elevation={0}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.06),
                        }}
                      >
                        <TableCell colSpan={2} sx={{ fontWeight: 700 }}>Артикул / описание</TableCell>
                        <TableCell width="120px" sx={{ fontWeight: 700 }}>Количество</TableCell>
                        <TableCell width="150px" sx={{ fontWeight: 700 }}>Мярка</TableCell>
                        <TableCell width="150px" sx={{ fontWeight: 700 }}>
                          {pricesIncludeVat ? 'Ед. цена с ДДС' : 'Ед. цена без ДДС'}
                        </TableCell>
                        <TableCell width="180px" sx={{ fontWeight: 700 }}>ДДС %</TableCell>
                        <TableCell width="150px" sx={{ fontWeight: 700 }}>Общо</TableCell>
                        <TableCell width="60px" sx={{ fontWeight: 700 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const availableItems = itemsData?.activeItemsByCompany || [];
                        const currentItem = availableItems.find(i => i.id === item.itemId) || null;
                        return (
                          <TableRow key={item.id} hover>
                            <TableCell colSpan={2}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => openItemSearchModal(index)}
                                    sx={{
                                      justifyContent: 'flex-start',
                                      textAlign: 'left',
                                      color: currentItem ? 'text.primary' : 'text.secondary',
                                      borderStyle: currentItem ? 'solid' : 'dashed',
                                      textTransform: 'none',
                                      py: 1,
                                      fontWeight: currentItem ? 600 : 500,
                                    }}
                                  >
                                    {currentItem
                                      ? `${currentItem.itemNumber ? `${currentItem.itemNumber} — ` : ''}${currentItem.name}`
                                      : 'Изберете артикул'}
                                  </Button>
                                  {currentItem && (
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        handleItemChange(index, 'itemId', null);
                                        handleItemChange(index, 'articleNumber', '');
                                        handleItemChange(index, 'description', '');
                                        handleItemChange(index, 'priceWithoutVat', 0);
                                        handleItemChange(index, 'unit', 'C62');
                                      }}
                                      sx={{ color: 'text.secondary' }}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  )}
                                </Box>
                                
                                {item.vatPercent === 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <VatExemptionAutocomplete
                                      value={item.vatExemptionReasonId}
                                      onChange={(reasonId) => handleItemChange(index, 'vatExemptionReasonId', reasonId)}
                                      size="small"
                                      required={item.vatPercent === 0}
                                      label="Основание за 0% ДДС"
                                    />
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={item.quantity || ''}
                                onChange={(e) => handleItemChange(index, 'quantity', handleDecimalInput(e.target.value))}
                                size="small"
                                fullWidth
                                placeholder="0"
                                inputProps={{
                                  step: 0.01,
                                  style: { MozAppearance: 'textfield' }
                                }}
                                sx={{
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={getUnitName(item.unit)}
                                size="small"
                                fullWidth
                                disabled
                                InputProps={{
                                  readOnly: true,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={pricesIncludeVat ? (item.priceWithVat || '') : (item.priceWithoutVat || '')}
                                onChange={(e) => handleItemChange(
                                  index,
                                  pricesIncludeVat ? 'priceWithVat' : 'priceWithoutVat',
                                  handleDecimalInput(e.target.value)
                                )}
                                size="small"
                                fullWidth
                                placeholder="0.00"
                                inputProps={{
                                  step: 0.01,
                                  style: { MozAppearance: 'textfield' }
                                }}
                                sx={{
                                  '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                  },
                                  '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                  '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell>
                                <Select
                                  value={item.vatPercent !== undefined ? item.vatPercent : 20}
                                  onChange={(e) => handleItemChange(index, 'vatPercent', Number(e.target.value))}
                                  size="small"
                                  fullWidth
                                  sx={{ height: 40 }}
                                  variant="standard"
                                  disableUnderline
                                >
                                  {vatRatesData?.activeVatRates.map((rate) => (
                                    <MenuItem key={rate.id} value={rate.rateValue}>
                                      {rate.rateValue}%
                                    </MenuItem>
                                  ))}
                                </Select>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {item.totalValue.toFixed(2)} {formData.currencyCode}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton onClick={() => removeItem(index)} disabled={formData.items.length === 1} color="error" size="small">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
          </Section>

          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Section
                icon={<PaymentsIcon />}
                title="Плащане и бележки"
                subtitle="Начин на плащане, срок и допълнителна информация"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Метод на плащане</InputLabel>
                      <Select
                        value={formData.paymentMethodId}
                        label="Метод на плащане"
                        onChange={(e) => {
                          const paymentMethodId = e.target.value;
                          const paymentMethod = paymentMethodsData?.paymentMethodsByCompany.find(pm => pm.id === paymentMethodId);
                          handleInputChange('paymentMethodId', paymentMethodId);
                          setSelectedPaymentMethod(paymentMethod);
                          if (!paymentMethod?.requiresBankAccount) {
                            handleInputChange('bankAccountId', '');
                          }
                        }}
                        disabled={paymentMethodsLoading}
                      >
                        {paymentMethodsData?.paymentMethodsByCompany.map((method) => (
                          <MenuItem key={method.id} value={method.id}>
                            {method.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {selectedPaymentMethod?.requiresBankAccount && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Банкова сметка</InputLabel>
                        <Select
                          value={formData.bankAccountId}
                          label="Банкова сметка"
                          onChange={(e) => handleInputChange('bankAccountId', e.target.value)}
                          disabled={bankAccountsLoading}
                        >
                          {bankAccountsData?.bankAccountsByCompany.filter(ba => ba.isActive).map((account) => (
                            <MenuItem key={account.id} value={account.id}>
                              {account.displayName || account.accountName} ({account.currencyCode})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Срок за плащане (дни)"
                      type="number"
                      value={formData.paymentTerms}
                      onChange={(e) => handleInputChange('paymentTerms', Number(e.target.value))}
                      fullWidth
                      sx={numberInputSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Бележки"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      multiline
                      rows={3}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Section>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 3,
                  height: '100%',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.04),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      color: 'primary.main',
                    }}
                  >
                    <CalculateIcon />
                  </Box>
                  <Typography variant="h6">Обобщение</Typography>
                </Box>
                <Stack spacing={1.25}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography color="text.secondary">Сума без отстъпка</Typography>
                    <Typography>{formData.subtotalWithoutDiscount.toFixed(2)} {formData.currencyCode}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography color="text.secondary">Отстъпка</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        type="number"
                        value={formData.discountPercent}
                        onChange={(e) => handleInputChange('discountPercent', handleDecimalInput(e.target.value))}
                        size="small"
                        sx={{ width: 80, ...numberInputSx }}
                        inputProps={{ min: 0, max: 100 }}
                      />
                      <Typography>%</Typography>
                      <Typography color="text.secondary">{formData.discountAmount.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>Данъчна основа</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formData.taxableAmount.toFixed(2)} {formData.currencyCode}</Typography>
                  </Box>
                  {Object.values(formData.vatBreakdown || {}).sort((a, b) => b.rate - a.rate).map((vatInfo) => (
                    <Box key={vatInfo.rate} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">ДДС ({vatInfo.rate}%)</Typography>
                      <Typography>{vatInfo.amount.toFixed(2)} {formData.currencyCode}</Typography>
                    </Box>
                  ))}
                  {(!formData.vatBreakdown || Object.keys(formData.vatBreakdown).length === 0) && formData.vatAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Typography color="text.secondary">ДДС</Typography>
                      <Typography>{formData.vatAmount.toFixed(2)} {formData.currencyCode}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 2,
                      pt: 0.5,
                    }}
                  >
                    <Typography variant="h6">За плащане</Typography>
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                      {formData.totalAmount.toFixed(2)} {formData.currencyCode}
                    </Typography>
                  </Box>
                  {formData.currencyCode !== 'EUR' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Общо в EUR (курс {formData.conversionRate?.toFixed(5)}
                        {formData.conversionRateDate && ` от ${new Date(formData.conversionRateDate).toLocaleDateString()}`})
                      </Typography>
                      {exchangeRateLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {formData.totalAmountInEUR.toFixed(2)} EUR
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Paper
            sx={{
              position: 'sticky',
              bottom: 12,
              zIndex: 8,
              p: 2,
              borderRadius: 3,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              flexWrap: 'wrap',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: (theme) => theme.palette.mode === 'dark'
                ? '0 -8px 24px rgba(0,0,0,0.35)'
                : '0 -8px 24px rgba(15,23,42,0.08)',
            }}
          >
            <Button variant="outlined" color="secondary" onClick={() => navigate('/documents')} startIcon={<CancelIcon />}>
              Отказ
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            >
              Създай {DOCUMENT_TYPE_LABELS[formData.documentType]?.toLowerCase() || 'документ'}
            </Button>
          </Paper>
        </Stack>
      </form>



      <ItemSearchModal
        open={isItemSearchModalOpen}
        onClose={() => setIsItemSearchModalOpen(false)}
        onItemSelected={handleItemSelected}
        items={itemsData?.activeItemsByCompany || []}
        loading={itemsLoading}
        companyId={activeCompanyId || '1'}
        onItemAdded={refetchItems}
      />
    </Box>
  );
};

export default CreateInvoiceNew;